'use client';

/**
 * A card lit from wherever the pointer is.
 *
 * After React Bits' SpotlightCard (MIT + Commons Clause, used inside the
 * product as its licence allows): a soft pool of the accent follows the
 * pointer across the card's surface, so a row of choices responds to being
 * considered before any is pressed. The light is written straight to one
 * layer's background (no re-render per move) and fades on the short fade;
 * with reduced motion, or on touch, the card simply has no light.
 */

import { useRef, type PointerEvent, type ReactNode } from 'react';

export default function SpotlightCard({ children, className = '' }: { children: ReactNode; className?: string }) {
  const light = useRef<HTMLSpanElement>(null);

  const move = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse' || !light.current) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    light.current.style.background =
      `radial-gradient(320px circle at ${x}px ${y}px, color-mix(in oklab, var(--rt-accent) 13%, transparent), transparent 70%)`;
  };

  return (
    <div
      onPointerMove={move}
      className={`group/spot relative isolate overflow-hidden ${className}`}
    >
      <span
        ref={light}
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-0 transition-opacity duration-[var(--rt-fade-ms)] group-hover/spot:opacity-100 motion-reduce:hidden"
      />
      {children}
    </div>
  );
}
